import React, { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

/**
 * UserAvatar component - A consistent avatar component for displaying user avatars
 * @param {Object} props
 * @param {Object} props.user - User object containing firstName, lastName, and avatar
 * @param {string} props.size - Size class for the avatar (e.g., "h-8 w-8", "h-12 w-12")
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showFullName - Whether to show full name as tooltip (default: false)
 */
const UserAvatar = ({
  user,
  size = "h-8 w-8",
  className = "",
  showFullName = true,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Generate fallback initials
  const getInitials = (user) => {
    if (user?.firstName) {
      return user.firstName[0].toUpperCase();
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return "U";
  };

  // Get full name for tooltip
  const getFullName = (user) => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.firstName) {
      return user.firstName;
    }
    if (user?.email) {
      return user.email;
    }
    return "Unknown User";
  };

  const hasValidAvatar = user?.avatar && !imageError;

  const avatarElement = (
    <Avatar className={`${size} ${className}`}>
      {hasValidAvatar && (
        <AvatarImage
          src={user.avatar}
          alt={getFullName(user)}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
          style={{
            display: imageLoaded && !imageError ? "block" : "none",
          }}
        />
      )}
      {(!hasValidAvatar || !imageLoaded) && (
        <AvatarFallback>{getInitials(user)}</AvatarFallback>
      )}
    </Avatar>
  );

  // Wrap with tooltip if showFullName is true
  if (showFullName) {
    return <div title={getFullName(user)}>{avatarElement}</div>;
  }

  return avatarElement;
};

export default UserAvatar;
